import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import api from "../../../../shared/api/apiClient";
import "./ContactImportPage.css";

const FIELD_TYPES = ["text", "number", "date", "boolean", "select"];

function inferFieldTypeFromSamples(rows = [], header = "") {
  const values = rows
    .map((row) => String(row?.[header] ?? "").trim())
    .filter(Boolean)
    .slice(0, 8);

  if (!values.length) return "text";

  const lowerValues = values.map((x) => x.toLowerCase());
  const booleanWords = new Set(["true", "false", "yes", "no", "да", "нет", "1", "0", "active", "inactive"]);
  if (lowerValues.every((x) => booleanWords.has(x))) return "boolean";

  const normalizedNumbers = values.map((x) => x.replace(/\s/g, "").replace(",", "."));
  if (normalizedNumbers.every((x) => /^-?\d+(\.\d+)?$/.test(x))) return "number";

  const dateLike = values.every((x) => (
    /^\d{4}-\d{2}-\d{2}/.test(x) ||
    /^\d{2}\.\d{2}\.\d{4}/.test(x) ||
    /^\d{2}\/\d{2}\/\d{4}/.test(x)
  ));
  if (dateLike) return "date";

  const uniqueCount = new Set(values).size;
  if (values.length >= 4 && uniqueCount <= 6) return "select";

  return "text";
}

function getUniqueSampleOptions(rows = [], header = "") {
  return Array.from(new Set(rows.map((row) => String(row?.[header] ?? "").trim()).filter(Boolean))).slice(0, 30);
}

function getErrorMessage(err, fallback) {
  const detail = err?.response?.data?.detail;
  if (Array.isArray(detail)) return detail.map((x) => x?.msg || JSON.stringify(x)).join(", ");
  if (detail && typeof detail === "object") return JSON.stringify(detail);
  return detail || err?.message || fallback;
}

function makeKey(value) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/gi, "_")
    .replace(/^_+|_+$/g, "")
    .slice(0, 80);
}


function normalizeCustomFieldForImport(field) {
  const options = field?.options_json || field?.options || {};
  const labelI18n = options?.label_i18n || {};
  return {
    id: field?.id,
    key: field?.key,
    label: field?.label || labelI18n?.ru || labelI18n?.en || field?.key,
    data_type: field?.data_type || "text",
    is_required: Boolean(field?.is_required),
    is_searchable: field?.is_searchable !== false,
    is_visible_in_table: field?.is_visible_in_table !== false,
  };
}

function mergeCustomFields(primary = [], secondary = []) {
  const map = new Map();
  [...primary, ...secondary].forEach((field) => {
    const normalized = normalizeCustomFieldForImport(field);
    if (normalized.key) map.set(normalized.key, normalized);
  });
  return Array.from(map.values());
}

function MappingTargetDropdown({ value, onChange, systemFields = [], customFields = [], importMode = "contacts", allowCustom = true, stageEntity = "contact", disabled = false, disabledReason = "" }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [menuStyle, setMenuStyle] = useState({});
  const wrapRef = useRef(null);
  const btnRef = useRef(null);
  const menuRef = useRef(null);

  const normalizedSearch = search.trim().toLowerCase();
  const allowedEntity = (field) => {
    const entity = field.entity || "contact";
    if (entity === "contact") return true;
    if (entity === "lead") return importMode === "contacts_leads" || importMode === "contacts_leads_tasks";
    if (entity === "task") return importMode === "contacts_leads_tasks";
    return true;
  };
  const filteredSystem = systemFields.filter((field) =>
    allowedEntity(field) && `${field.label || ""} ${field.key || ""}`.toLowerCase().includes(normalizedSearch)
  );
  const groupedSystem = {
    contact: filteredSystem.filter((field) => (field.entity || "contact") === "contact"),
    lead: filteredSystem.filter((field) => field.entity === "lead"),
    task: filteredSystem.filter((field) => field.entity === "task"),
  };
  const filteredCustom = allowCustom ? customFields.filter((field) =>
    `${field.label || ""} ${field.key || ""}`.toLowerCase().includes(normalizedSearch)
  ) : [];

  const selectedMeta = useMemo(() => {
    const [type, key] = String(value || "skip:").split(":");
    if (type === "system") {
      const field = systemFields.find((x) => x.key === key);
      const entity = field?.entity || "contact";
      return {
        badge: field?.required
          ? t("contact_import_required_badge")
          : field?.require_one_of
            ? t("contact_import_email_or_phone_badge")
            : t(`contact_import_${entity}_badge`),
        badgeClass: field?.required ? "required" : field?.require_one_of ? "orRequired" : entity === "lead" ? "lead" : entity === "task" ? "task" : "system",
        label: field?.label || key || t("contact_import_system_field"),
      };
    }
    if (type === "custom") {
      const field = customFields.find((x) => x.key === key);
      return { badge: t("contact_import_dynamic_badge"), badgeClass: "custom", label: field?.label || key || t("contact_import_dynamic_field") };
    }
    if (type === "create_custom") return { badge: t("contact_import_new_badge"), badgeClass: "new", label: t("contact_import_create_new_field") };
    return { badge: t("contact_import_skip_badge"), badgeClass: "skip", label: t("contact_import_do_not_import") };
  }, [value, systemFields, customFields, t]);

  const updateMenuPosition = () => {
    const rect = btnRef.current?.getBoundingClientRect();
    if (!rect) return;
    const viewportPadding = 12;
    const width = Math.max(rect.width, Math.min(520, window.innerWidth - viewportPadding * 2));
    const left = Math.min(Math.max(rect.left, viewportPadding), window.innerWidth - width - viewportPadding);
    const spaceBelow = window.innerHeight - rect.bottom - viewportPadding;
    const spaceAbove = rect.top - viewportPadding;
    const openUp = spaceBelow < 260 && spaceAbove > spaceBelow;
    const maxHeight = Math.max(220, Math.min(380, openUp ? spaceAbove - 8 : spaceBelow - 8));

    setMenuStyle({
      position: "fixed",
      left: `${left}px`,
      top: openUp ? "auto" : `${rect.bottom + 6}px`,
      bottom: openUp ? `${window.innerHeight - rect.top + 6}px` : "auto",
      width: `${width}px`,
      maxHeight: `${maxHeight}px`,
      zIndex: 99999,
    });
  };

  useEffect(() => {
    if (!open) return undefined;
    updateMenuPosition();

    const handlePointerDown = (event) => {
      if (wrapRef.current?.contains(event.target) || menuRef.current?.contains(event.target)) return;
      setOpen(false);
    };
    const handleReposition = () => updateMenuPosition();

    document.addEventListener("mousedown", handlePointerDown);
    window.addEventListener("resize", handleReposition);
    window.addEventListener("scroll", handleReposition, true);

    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      window.removeEventListener("resize", handleReposition);
      window.removeEventListener("scroll", handleReposition, true);
    };
  }, [open]);

  const choose = (nextValue) => {
    onChange(nextValue);
    setOpen(false);
    setSearch("");
  };

  const menu = open ? createPortal(
    <div ref={menuRef} className="contactImportTargetDropdownMenu contactImportTargetDropdownMenuPortal" style={menuStyle}>
      <input
        className="contactImportTargetDropdownSearch"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder={t("contact_import_search_field")}
        autoFocus
      />

      <button type="button" className="contactImportTargetOption is-skip" onClick={() => choose("skip:")}>
        <span className="contactImportTargetBadge skip">{t("contact_import_skip_badge")}</span>
        <span>{t("contact_import_skip_column")}</span>
      </button>

      {[
        ["contact", "contact_import_stage_contact"],
        ["lead", "contact_import_stage_lead"],
        ["task", "contact_import_stage_task"],
      ].map(([entity, title]) => groupedSystem[entity]?.length ? (
        <React.Fragment key={entity}>
          <div className="contactImportTargetGroupTitle">{t(title)}</div>
          {groupedSystem[entity].map((field) => {
            const badgeClass = field.required ? "required" : field.require_one_of ? "orRequired" : entity === "lead" ? "lead" : entity === "task" ? "task" : "system";
            return (
              <button
                type="button"
                className={`contactImportTargetOption ${field.required ? "is-required" : "is-core"} ${value === `system:${field.key}` ? "is-active" : ""}`}
                key={`system:${field.key}`}
                onClick={() => choose(`system:${field.key}`)}
              >
                <span className={`contactImportTargetBadge ${badgeClass}`}>
                  {field.required
                    ? t("contact_import_required_badge")
                    : field.require_one_of
                      ? t("contact_import_email_or_phone_badge")
                      : t(`contact_import_${entity}_badge`)}
                </span>
                <span>{field.label || field.key}</span>
                <small>{field.key}</small>
              </button>
            );
          })}
        </React.Fragment>
      ) : null)}

      {allowCustom && (
        <>
          <div className="contactImportTargetGroupTitle">{t("contact_import_dynamic_fields")}</div>
          {filteredCustom.length ? filteredCustom.map((field) => (
            <button
              type="button"
              className={`contactImportTargetOption is-custom ${value === `custom:${field.key}` ? "is-active" : ""}`}
              key={`custom:${field.key}`}
              onClick={() => choose(`custom:${field.key}`)}
            >
              <span className="contactImportTargetBadge custom">{t("contact_import_dynamic_badge")}</span>
              <span>✨ {field.label || field.key}</span>
              <small>{field.key}</small>
            </button>
          )) : (
            <div className="contactImportTargetEmpty">{t("contact_import_no_dynamic_fields")}</div>
          )}

          <div className="contactImportTargetGroupTitle">{t("actions")}</div>
          <button type="button" className="contactImportTargetOption is-new" onClick={() => choose("create_custom:")}>
            <span className="contactImportTargetBadge new">{t("contact_import_new_badge")}</span>
            <span>{t("contact_import_create_dynamic_field")}</span>
          </button>
        </>
      )}
    </div>,
    document.body
  ) : null;

  return (
    <div className="contactImportTargetDropdown" ref={wrapRef}>
      <button
        ref={btnRef}
        type="button"
        className={`contactImportTargetDropdownBtn is-${selectedMeta.badgeClass}`}
        onClick={() => { if (!disabled) setOpen((x) => !x); }}
        disabled={disabled}
        title={disabledReason || undefined}
      >
        <span className={`contactImportTargetBadge ${selectedMeta.badgeClass}`}>{selectedMeta.badge}</span>
        <span className="contactImportTargetDropdownValue">{selectedMeta.label}</span>
        <b>{disabled ? "×" : open ? "⌃" : "⌄"}</b>
      </button>
      {menu}
    </div>
  );
}

function ContactImportPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);

  const [selectedFile, setSelectedFile] = useState(null);
  const [selectedFileName, setSelectedFileName] = useState("");
  const [preview, setPreview] = useState(null);
  const [mappings, setMappings] = useState([]);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState("");
  const [uploadResult, setUploadResult] = useState(null);
  const [wizardStep, setWizardStep] = useState("contact");
  const [leadMappings, setLeadMappings] = useState([]);
  const [taskMappings, setTaskMappings] = useState([]);
  const [includeLeads, setIncludeLeads] = useState(false);
  const [includeTasks, setIncludeTasks] = useState(false);
  const [taskDetailSources, setTaskDetailSources] = useState([]);

  const systemFieldMap = useMemo(() => {
    const map = new Map();
    (preview?.system_fields || []).forEach((field) => map.set(field.key, field));
    return map;
  }, [preview]);

  const customFieldMap = useMemo(() => {
    const map = new Map();
    (preview?.custom_fields || []).forEach((field) => map.set(field.key, field));
    return map;
  }, [preview]);

  const mappingStats = useMemo(() => {
    const required = (preview?.system_fields || []).filter((field) => field.required);
    const mappedRequired = required.filter((field) => mappings.some((m) => m.target_type === "system" && m.target_field === field.key));
    return {
      total: mappings.length,
      mapped: mappings.filter((m) => m.target_type !== "skip").length,
      system: mappings.filter((m) => m.target_type === "system").length,
      lead: mappings.filter((m) => m.target_type === "system" && (systemFieldMap.get(m.target_field)?.entity === "lead")).length,
      task: mappings.filter((m) => m.target_type === "system" && (systemFieldMap.get(m.target_field)?.entity === "task")).length,
      custom: mappings.filter((m) => m.target_type === "custom").length,
      newFields: mappings.filter((m) => m.target_type === "create_custom").length,
      requiredTotal: required.length,
      requiredMapped: mappedRequired.length,
    };
  }, [preview, mappings, systemFieldMap]);

  const getMappingMeta = (mapping) => {
    if (mapping.target_type === "system") {
      const field = systemFieldMap.get(mapping.target_field);
      const entity = field?.entity || "contact";
      return {
        kind: field?.required ? "required" : field?.require_one_of ? "orRequired" : entity === "lead" ? "lead" : entity === "task" ? "task" : "system",
        badge: field?.required
          ? t("contact_import_required_badge")
          : field?.require_one_of
            ? t("contact_import_email_or_phone_badge")
            : t(`contact_import_${entity}_badge`),
        title: field?.label || mapping.target_field,
        icon: field?.required ? "🔵" : field?.require_one_of ? "📧/📞" : entity === "lead" ? "🎯" : entity === "task" ? "✅" : "👤",
      };
    }
    if (mapping.target_type === "custom") {
      const field = customFieldMap.get(mapping.target_field);
      return { kind: "custom", badge: t("contact_import_dynamic_badge"), title: field?.label || mapping.target_field, icon: "✨" };
    }
    if (mapping.target_type === "create_custom") {
      return { kind: "new", badge: t("contact_import_new_field_badge"), title: mapping.label_ru || mapping.csv_header, icon: "＋" };
    }
    return { kind: "skip", badge: t("contact_import_skip_badge"), title: t("contact_import_do_not_import"), icon: "—" };
  };

  const downloadTemplateCsv = () => {
    window.open("https://drive.google.com/uc?export=download&id=18LeD-8jNllmk3rnWf_2mKtheLnHvAvc-", "_blank");
  };

  const getFieldEntity = (key) => systemFieldMap.get(key)?.entity || "contact";

  const buildMappingFromPreview = (data, entity = "contact") => {
    const suggestionsByHeader = new Map((data.suggestions || []).map((x) => [x.csv_header, x]));
    return (data.headers || []).map((header) => {
      const suggestion = suggestionsByHeader.get(header) || {};
      const suggestedField = suggestion.target_field || makeKey(header);
      const suggestedSystemField = (data.system_fields || []).find((field) => field.key === suggestedField);
      const suggestedEntity = suggestedSystemField?.entity || (suggestion.target_type === "custom" || suggestion.target_type === "create_custom" ? "contact" : "contact");

      let targetType = "skip";
      let key = "";

      if (entity === "contact") {
        if (suggestion.target_type === "system" && suggestedEntity === "contact") {
          targetType = "system";
          key = suggestedField;
        } else if (suggestion.target_type === "custom") {
          targetType = "custom";
          key = suggestedField;
        } else if (suggestion.target_type === "create_custom") {
          targetType = "create_custom";
          key = makeKey(suggestedField || header);
        } else {
          targetType = "skip";
          key = "";
        }
      } else if (suggestion.target_type === "system" && suggestedEntity === entity) {
        targetType = "system";
        key = suggestedField;
      }

      return {
        csv_header: header,
        target_type: targetType,
        target_field: targetType === "create_custom" ? makeKey(key || header) : key,
        data_type: targetType === "create_custom" ? inferFieldTypeFromSamples(data.sample_rows || [], header) : "text",
        label_ru: header,
        label_en: header,
        default_value: "",
        is_required: false,
        is_searchable: true,
        is_visible_in_table: true,
        is_visible_in_card: true,
        allow_filtering: true,
        select_options: getUniqueSampleOptions(data.sample_rows || [], header),
      };
    });
  };

  const activeEntity = wizardStep;
  const activeMappings = wizardStep === "lead" ? leadMappings : wizardStep === "task" ? taskMappings : mappings;
  const activeSystemFields = useMemo(() => (preview?.system_fields || []).filter((field) => (field.entity || "contact") === activeEntity), [preview, activeEntity]);

  const getHeaderUsageBeforeStage = (header, stage = wizardStep) => {
    const contactMapping = mappings.find((m) => m.csv_header === header && m.target_type !== "skip");
    if ((stage === "lead" || stage === "task") && contactMapping) {
      const meta = getMappingMeta(contactMapping);
      return { stage: "contact", label: t("contact_import_stage_contact"), meta, mapping: contactMapping };
    }

    const leadMapping = leadMappings.find((m) => m.csv_header === header && m.target_type !== "skip");
    if (stage === "task" && includeLeads && leadMapping) {
      const meta = getMappingMeta(leadMapping);
      return { stage: "lead", label: t("contact_import_stage_lead"), meta, mapping: leadMapping };
    }

    return null;
  };

  const stripMappingsUsedByPreviousStages = (stageMappings, stage) =>
    stageMappings.map((item) => getHeaderUsageBeforeStage(item.csv_header, stage) ? { ...item, target_type: "skip", target_field: "" } : item);

  const stageTitle = wizardStep === "lead" ? t("contact_import_stage_leads") : wizardStep === "task" ? t("contact_import_stage_tasks") : t("contact_import_stage_contacts");
  const stageHint = wizardStep === "lead"
    ? t("contact_import_stage_lead_hint")
    : wizardStep === "task"
      ? t("contact_import_stage_task_hint")
      : t("contact_import_stage_contact_hint");

  const updateStageMapping = (stage, index, patch) => {
    const setter = stage === "lead" ? setLeadMappings : stage === "task" ? setTaskMappings : setMappings;
    setter((prev) =>
      prev.map((item, idx) => {
        if (idx !== index) return item;
        if (stage !== "contact" && getHeaderUsageBeforeStage(item.csv_header, stage)) return item;
        const next = { ...item, ...patch };
        if (patch.targetCombined) {
          const [targetType, targetField] = patch.targetCombined.split(":");
          next.target_type = targetType || "skip";
          next.target_field = targetType === "create_custom" ? makeKey(item.csv_header) : targetField || "";
          delete next.targetCombined;
        }
        if (patch.label_ru !== undefined && next.target_type === "create_custom" && !next.target_field) {
          next.target_field = makeKey(patch.label_ru || next.csv_header);
        }
        return next;
      })
    );
  };

  const activeStageStats = useMemo(() => {
    const active = (activeMappings || []).filter((m) => !getHeaderUsageBeforeStage(m.csv_header, wizardStep));
    const locked = (activeMappings || []).length - active.length;
    const required = activeSystemFields.filter((field) => field.required);
    const mappedRequired = required.filter((field) => active.some((m) => m.target_type === "system" && m.target_field === field.key));
    const contactMethodFields = activeSystemFields.filter((field) => field.require_one_of === "contact_method");
    const mappedContactMethod = active.some((m) => m.target_type === "system" && contactMethodFields.some((f) => f.key === m.target_field));
    return {
      total: active.length,
      locked,
      mapped: active.filter((m) => m.target_type !== "skip").length,
      system: active.filter((m) => m.target_type === "system").length,
      custom: active.filter((m) => m.target_type === "custom").length,
      newFields: active.filter((m) => m.target_type === "create_custom").length,
      requiredTotal: required.length + (contactMethodFields.length ? 1 : 0),
      requiredMapped: mappedRequired.length + (contactMethodFields.length && mappedContactMethod ? 1 : 0),
      contactMethodRequired: Boolean(contactMethodFields.length),
      contactMethodMapped: mappedContactMethod,
    };
  }, [activeMappings, activeSystemFields, wizardStep, mappings, leadMappings, includeLeads]);

  const completionPercent = activeStageStats.total ? Math.round((activeStageStats.mapped / activeStageStats.total) * 100) : 0;

  const previousStageDetailOptions = useMemo(() => {
    const build = (stage, stageLabel, stageMappings) => (stageMappings || [])
      .filter((m) => m.target_type !== "skip")
      .map((m) => {
        const meta = getMappingMeta(m);
        return {
          id: `${stage}:${m.csv_header}`,
          stage,
          stage_label: stageLabel,
          csv_header: m.csv_header,
          label: meta.title || m.label_ru || m.csv_header,
          badge: meta.badge || stageLabel.toUpperCase(),
        };
      });

    return [
      ...build("contact", t("contact_import_stage_contact"), mappings),
      ...(includeLeads ? build("lead", t("contact_import_stage_lead"), leadMappings) : []),
    ];
  }, [mappings, leadMappings, includeLeads, systemFieldMap, customFieldMap, t]);

  const toggleTaskDetailSource = (source) => {
    setTaskDetailSources((prev) => {
      const exists = prev.some((item) => item.id === source.id);
      if (exists) return prev.filter((item) => item.id !== source.id);
      return [...prev, source];
    });
  };

  const selectAllTaskDetailSources = () => {
    setTaskDetailSources(previousStageDetailOptions);
  };

  const clearTaskDetailSources = () => {
    setTaskDetailSources([]);
  };

  const handleCsvSelected = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setSelectedFile(file);
    setSelectedFileName(file.name);
    setPreview(null);
    setMappings([]);
    setLeadMappings([]);
    setTaskMappings([]);
    setWizardStep("contact");
    setIncludeLeads(false);
    setIncludeTasks(false);
    setTaskDetailSources([]);
    setUploadResult(null);
    setUploadError("");
    setLoadingPreview(true);

    try {
      const formData = new FormData();
      formData.append("file", file);
      const { data } = await api.post("/contacts/import-csv/preview", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      let settingsCustomFields = [];
      try {
        const settingsResponse = await api.get("/custom-fields/contact");
        settingsCustomFields = Array.isArray(settingsResponse.data) ? settingsResponse.data : [];
      } catch (customErr) {
        console.warn("Custom fields fallback request failed", customErr);
      }

      const enrichedPreview = {
        ...data,
        custom_fields: mergeCustomFields(data.custom_fields || [], settingsCustomFields),
      };

      setPreview(enrichedPreview);
      setMappings(buildMappingFromPreview(enrichedPreview, "contact"));
      setLeadMappings(buildMappingFromPreview(enrichedPreview, "lead"));
      setTaskMappings(buildMappingFromPreview(enrichedPreview, "task"));
    } catch (err) {
      console.error(err);
      setUploadError(getErrorMessage(err, t("error_import_contacts_csv")));
      setSelectedFile(null);
    } finally {
      setLoadingPreview(false);
      event.target.value = "";
    }
  };

  const markAllUnknownAsDynamic = () => {
    setMappings((prev) =>
      prev.map((item) =>
        item.target_type === "create_custom"
          ? {
              ...item,
              target_field: item.target_field || makeKey(item.csv_header),
              label_ru: item.label_ru || item.csv_header,
              label_en: item.label_en || item.csv_header,
              data_type: item.data_type || inferFieldTypeFromSamples(preview?.sample_rows || [], item.csv_header),
              default_value: item.default_value || "",
              is_required: Boolean(item.is_required),
              is_searchable: item.is_searchable !== false,
              is_visible_in_table: item.is_visible_in_table !== false,
              is_visible_in_card: item.is_visible_in_card !== false,
              allow_filtering: item.allow_filtering !== false,
              select_options: item.select_options?.length ? item.select_options : getUniqueSampleOptions(preview?.sample_rows || [], item.csv_header),
            }
          : item
      )
    );
  };

  const validateMappings = () => {
    const active = mappings.filter((m) => m.target_type !== "skip");
    const systemTargets = active.filter((m) => m.target_type === "system").map((m) => m.target_field);
    if (!systemTargets.includes("full_name")) return t("contact_import_validation_full_name");
    if (!systemTargets.includes("emails") && !systemTargets.includes("phones")) return t("contact_import_validation_contact_method");
    const duplicates = systemTargets.filter((field, idx) => field && systemTargets.indexOf(field) !== idx);
    if (duplicates.length) return t("contact_import_validation_duplicate_system_field", { field: duplicates[0] });
    for (const m of active) {
      if (m.target_type === "create_custom") {
        if (!m.target_field || m.target_field.length < 2) return t("contact_import_validation_new_field_key", { column: m.csv_header });
        if (!m.label_ru?.trim() || !m.label_en?.trim()) return t("contact_import_validation_new_field_names", { column: m.csv_header });
      }
    }
    return "";
  };

  const handleImport = async (mode = null) => {
    if (!selectedFile) return;
    const shouldIncludeLeads = mode ? mode !== "contacts" : includeLeads;
    const shouldIncludeTasks = mode ? mode === "contacts_leads_tasks" : includeTasks;
    const finalMode = shouldIncludeTasks ? "contacts_leads_tasks" : shouldIncludeLeads ? "contacts_leads" : "contacts";
    const safeLeadMappings = shouldIncludeLeads ? stripMappingsUsedByPreviousStages(leadMappings, "lead") : [];
    const safeTaskMappings = shouldIncludeTasks ? stripMappingsUsedByPreviousStages(taskMappings, "task") : [];
    const finalMappings = [
      ...mappings,
      ...safeLeadMappings,
      ...safeTaskMappings,
    ];

    const validationError = validateMappings();
    if (validationError) {
      setWizardStep("contact");
      setUploadError(validationError);
      return;
    }

    setUploading(true);
    setUploadError("");
    setUploadResult(null);

    try {
      const formData = new FormData();
      formData.append("file", selectedFile);
      formData.append("mapping_json", JSON.stringify({ mappings: finalMappings, import_mode: finalMode, task_detail_sources: shouldIncludeTasks ? taskDetailSources : [] }));

      const { data } = await api.post("/contacts/import-csv", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setUploadResult(data);
    } catch (err) {
      console.error(err);
      setUploadError(getErrorMessage(err, t("error_import_contacts_csv")));
    } finally {
      setUploading(false);
    }
  };

  const handleDownloadErrorCsv = () => {
    if (!uploadResult?.error_csv) return;
    const blob = new Blob([uploadResult.error_csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "contacts_import_errors.csv";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="contactImportPage">
      <div className="contactImportHeader">
        <div>
          <h1 className="contactImportTitle">{t("contact_import_title")}</h1>
          <p className="contactImportSubtitle">{t("contact_import_subtitle")}</p>
        </div>
        <button type="button" className="btn btn-outline-secondary contactImportBackBtn" onClick={() => navigate("/menu/sales/contacts")}>
          {t("contact_back_to_contacts")}
        </button>
      </div>

      <div className="contactImportGrid">
        <section className="contactImportCard contactImportMainCard">
          <div className="contactImportCardHeader">
            <div>
              <h2>{t("import_csv")}</h2>
              <p>{t("contacts_csv_hint")}</p>
            </div>
            <div className="contactImportInfoWrap">
              <button type="button" className="contactImportInfoBtn" aria-label={t("contact_import_info_aria")}>i</button>
              <div className="contactImportTooltip">
                <strong>{t("contact_import_csv_format_title")}</strong>
                <span>{t("contact_import_csv_format_text")}</span>
              </div>
            </div>
          </div>

          <div className="contactImportUploadBox">
            <input ref={fileInputRef} id="contactImportFile" type="file" accept=".csv" className="contactImportFileInput" onChange={handleCsvSelected} disabled={loadingPreview || uploading} />
            <label htmlFor="contactImportFile" className="contactImportFileLabel">
              <span className="contactImportFileIcon">↑</span>
              <span>
                <strong>{loadingPreview ? `${t("loading")}...` : t("contact_import_choose_csv_file")}</strong>
                <small>{selectedFileName || t("contact_import_no_file_selected")}</small>
              </span>
            </label>
          </div>

          {preview && (
            <div className="contactImportMappingBlock">
              <div className="contactImportWizardStepper">
                <button type="button" className={`contactImportWizardStep ${wizardStep === "contact" ? "is-active" : "is-done"}`} onClick={() => setWizardStep("contact")}>
                  <span>1</span><b>{t("sales_contacts")}</b><small>{t("contact_import_required_stage")}</small>
                </button>
                <button type="button" className={`contactImportWizardStep ${wizardStep === "lead" ? "is-active" : includeLeads ? "is-done" : ""}`} onClick={() => { setIncludeLeads(true); setWizardStep("lead"); }}>
                  <span>2</span><b>{t("leads")}</b><small>{t("crm_settings_optional")}</small>
                </button>
                <button type="button" className={`contactImportWizardStep ${wizardStep === "task" ? "is-active" : includeTasks ? "is-done" : ""}`} onClick={() => { setIncludeLeads(true); setIncludeTasks(true); setWizardStep("task"); }}>
                  <span>3</span><b>{t("tasks")}</b><small>{t("crm_settings_optional")}</small>
                </button>
              </div>
              <div className="contactImportMappingHeader contactImportMappingHeaderCompact">
                <div>
                  <h3>{stageTitle}</h3>
                  <p>{stageHint}</p>
                </div>
                <button type="button" className="btn btn-sm btn-outline-light" onClick={() => fileInputRef.current?.click()} disabled={uploading}>
                  {t("contact_import_replace_file")}
                </button>
              </div>

              <div className="contactImportStatsStrip">
                <div><strong>{activeStageStats.total}</strong><span>{t("contact_import_available")}</span></div>
                <div><strong>{activeStageStats.locked}</strong><span>{t("contact_import_already_used")}</span></div>
                <div><strong>{activeStageStats.requiredMapped}/{activeStageStats.requiredTotal}</strong><span>{t("contact_import_required")}</span></div>
                <div><strong>{activeStageStats.system}</strong><span>{t("contact_import_system")}</span></div>
                <div><strong>{activeStageStats.custom}</strong><span>{t("contact_import_dynamic")}</span></div>
                <div><strong>{activeStageStats.newFields}</strong><span>{t("contact_import_new")}</span></div>
              </div>

              {wizardStep === "contact" && activeStageStats.contactMethodRequired && (
                <div className={`contactImportUsedColumnsNotice ${activeStageStats.contactMethodMapped ? "is-ok" : "is-warning"}`}>
                  <strong>{activeStageStats.contactMethodMapped ? t("contact_import_minimum_requirements_met") : t("contact_import_choose_email_or_phone")}</strong>
                  <span>{t("contact_import_contact_channel_hint")}</span>
                </div>
              )}

              {wizardStep !== "contact" && activeStageStats.locked > 0 && (
                <div className="contactImportUsedColumnsNotice">
                  <strong>{t("contact_import_used_columns_locked")}</strong>
                  <span>{t("contact_import_columns_not_repeated")} {wizardStep === "lead" ? t("contact_import_stage_lead") : t("contact_import_stage_task")}{t("contact_import_columns_marked")}</span>
                </div>
              )}

              {wizardStep === "task" && previousStageDetailOptions.length > 0 && (
                <div className="contactImportTaskDetailsBuilder">
                  <div className="contactImportTaskDetailsHeader">
                    <div>
                      <strong>{t("contact_import_task_details")}</strong>
                      <span>{t("contact_import_task_details_hint")}</span>
                    </div>
                    <div className="contactImportTaskDetailsActions">
                      <button type="button" className="btn btn-sm btn-outline-light" onClick={selectAllTaskDetailSources}>{t("select_all")}</button>
                      <button type="button" className="btn btn-sm btn-outline-secondary" onClick={clearTaskDetailSources}>{t("clear")}</button>
                    </div>
                  </div>
                  <div className="contactImportTaskDetailsGrid">
                    {previousStageDetailOptions.map((source) => {
                      const checked = taskDetailSources.some((item) => item.id === source.id);
                      return (
                        <label className={`contactImportTaskDetailChip ${checked ? "is-selected" : ""}`} key={source.id}>
                          <input type="checkbox" checked={checked} onChange={() => toggleTaskDetailSource(source)} />
                          <span className={`contactImportTargetBadge ${source.stage === "lead" ? "lead" : "system"}`}>{source.stage_label}</span>
                          <b>{source.label}</b>
                          <small>{source.csv_header}</small>
                        </label>
                      );
                    })}
                  </div>
                  {taskDetailSources.length > 0 && (
                    <div className="contactImportTaskDetailsPreview">
                      <strong>{t("contact_import_task_description_additions")}</strong>
                      <span>{taskDetailSources.map((x) => `${x.stage_label} • ${x.label}: {${x.csv_header}}`).join("\n")}</span>
                    </div>
                  )}
                </div>
              )}

              {wizardStep === "contact" && activeStageStats.newFields > 0 && (
                <div className="contactImportNewFieldsNotice">
                  <div>
                    <strong>{t("contact_import_new_dynamic_found")} {activeStageStats.newFields}</strong>
                    <span>{t("contact_import_same_ru_en_hint")}</span>
                  </div>
                  <button type="button" className="btn btn-sm btn-success" onClick={markAllUnknownAsDynamic}>{t("contact_import_prepare_all")}</button>
                </div>
              )}

              <div className="contactImportMappingList contactImportMappingListCompact">
                {activeMappings.map((mapping, index) => {
                  const usedInfo = getHeaderUsageBeforeStage(mapping.csv_header, wizardStep);
                  const isLockedByPreviousStage = Boolean(usedInfo);
                  const combined = `${mapping.target_type}:${mapping.target_type === "create_custom" ? "" : mapping.target_field}`;
                  const lockedCombined = usedInfo?.mapping ? `${usedInfo.mapping.target_type}:${usedInfo.mapping.target_type === "create_custom" ? "" : usedInfo.mapping.target_field}` : combined;
                  const sample = preview.sample_rows?.find((row) => row?.[mapping.csv_header])?.[mapping.csv_header] || "";
                  const meta = isLockedByPreviousStage ? usedInfo.meta : getMappingMeta(mapping);
                  return (
                    <div className={`contactImportMappingRow contactImportMappingRowCompact is-${meta.kind} ${isLockedByPreviousStage ? "is-used-in-previous-stage" : ""}`} key={mapping.csv_header}>
                      <div className="contactImportCsvColumn contactImportCsvColumnCompact">
                        <span className="contactImportColumnBadge">{t("lead_origin_csv_label")}</span>
                        <strong>{mapping.csv_header}</strong>
                        {sample && <small title={sample}>{sample}</small>}
                        {isLockedByPreviousStage && <em className="contactImportUsedByBadge">{t("contact_import_already_used_as")} {usedInfo.label} {t("contact_import_information")}</em>}
                      </div>

                      <div className="contactImportArrow">→</div>

                      <div className="contactImportCrmColumn contactImportCrmColumnCompact">
                        <div className="contactImportSelectedField">
                          <span className={`contactImportFieldPill ${meta.kind}`}>{isLockedByPreviousStage ? usedInfo.label.toUpperCase() : meta.badge}</span>
                          <span className="contactImportFieldName"><b>{meta.icon}</b> {isLockedByPreviousStage ? t("contact_import_stage_information", { stage: usedInfo.label, field: meta.title }) : meta.title}</span>
                        </div>

                        <MappingTargetDropdown
                          value={isLockedByPreviousStage ? lockedCombined : combined}
                          systemFields={isLockedByPreviousStage ? (preview?.system_fields || []) : activeSystemFields}
                          customFields={isLockedByPreviousStage ? (preview?.custom_fields || []) : wizardStep === "contact" ? (preview?.custom_fields || []) : []}
                          importMode="contacts_leads_tasks"
                          allowCustom={wizardStep === "contact"}
                          stageEntity={wizardStep}
                          disabled={isLockedByPreviousStage}
                          disabledReason={isLockedByPreviousStage ? t("contact_import_column_used_at_stage", { stage: usedInfo.label }) : ""}
                          onChange={(nextValue) => updateStageMapping(wizardStep, index, { targetCombined: nextValue })}
                        />

                        {wizardStep === "contact" && !isLockedByPreviousStage && mapping.target_type === "create_custom" && (
                          <details className="contactImportNewFieldDetails">
                            <summary>{t("contact_import_configure_new_field")}</summary>
                            <div className="contactImportNewFieldEditor">
                              <div className="contactImportNewFieldEditorHeader">
                                <div>
                                  <strong>{t("contact_import_dynamic_field_settings")}</strong>
                                  <span>{t("contact_import_dynamic_field_settings_hint")}</span>
                                </div>
                              </div>

                              <div className="contactImportNewFieldGrid contactImportNewFieldGridCompact">
                                <div>
                                  <label>{t("contact_import_name_ru")}</label>
                                  <input value={mapping.label_ru} onChange={(e) => updateStageMapping(wizardStep, index, { label_ru: e.target.value })} />
                                </div>
                                <div>
                                  <label>{t("contact_import_name_en")}</label>
                                  <input value={mapping.label_en} onChange={(e) => updateStageMapping(wizardStep, index, { label_en: e.target.value })} />
                                </div>
                                <div>
                                  <label>{t("contact_custom_field_key")}</label>
                                  <input value={mapping.target_field} onChange={(e) => updateStageMapping(wizardStep, index, { target_field: makeKey(e.target.value) })} placeholder={t("contact_custom_field_key_placeholder")} />
                                </div>
                                <div>
                                  <label>{t("contact_type")}</label>
                                  <select value={mapping.data_type} onChange={(e) => updateStageMapping(wizardStep, index, { data_type: e.target.value })}>
                                    {FIELD_TYPES.map((type) => <option value={type} key={type}>{type}</option>)}
                                  </select>
                                </div>
                                <div className="contactImportFieldWide">
                                  <label>{t("contact_custom_field_default_value")}</label>
                                  <input value={mapping.default_value || ""} onChange={(e) => updateStageMapping(wizardStep, index, { default_value: e.target.value })} placeholder={t("contact_custom_field_default_value_placeholder")} />
                                </div>
                                {mapping.data_type === "select" && (
                                  <div className="contactImportFieldWide">
                                    <label>{t("contact_import_select_options")}</label>
                                    <textarea
                                      value={(mapping.select_options || []).join("\n")}
                                      onChange={(e) => updateStageMapping(wizardStep, index, { select_options: e.target.value.split("\n").map((x) => x.trim()).filter(Boolean) })}
                                      placeholder={t(
                                        "contact_import_select_options_placeholder",
                                      )}
                                    />
                                    <small>{t("contact_import_options_hint")}</small>
                                  </div>
                                )}
                              </div>

                              <div className="contactImportFieldToggles">
                                <label><input type="checkbox" checked={Boolean(mapping.is_required)} onChange={(e) => updateStageMapping(wizardStep, index, { is_required: e.target.checked })} /> {t("required")}</label>
                                <label><input type="checkbox" checked={mapping.is_searchable !== false} onChange={(e) => updateStageMapping(wizardStep, index, { is_searchable: e.target.checked })} /> {t("contact_custom_field_searchable")}</label>
                                <label><input type="checkbox" checked={mapping.is_visible_in_table !== false} onChange={(e) => updateStageMapping(wizardStep, index, { is_visible_in_table: e.target.checked })} /> {t("contact_custom_field_show_in_table")}</label>
                                <label><input type="checkbox" checked={mapping.is_visible_in_card !== false} onChange={(e) => updateStageMapping(wizardStep, index, { is_visible_in_card: e.target.checked })} /> {t("contact_import_show_contact_card")}</label>
                                <label><input type="checkbox" checked={mapping.allow_filtering !== false} onChange={(e) => updateStageMapping(wizardStep, index, { allow_filtering: e.target.checked })} /> {t("contact_import_allow_filtering")}</label>
                              </div>
                            </div>
                          </details>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="contactImportActions contactImportStepActions">
                {wizardStep === "contact" && (
                  <>
                    <button type="button" className="btn btn-primary" onClick={() => handleImport("contacts")} disabled={uploading || loadingPreview}>
                      {uploading ? `${t("loading")}...` : t("contact_import_only_contacts")}
                    </button>
                    <button type="button" className="btn btn-outline-primary" onClick={() => { setIncludeLeads(true); setWizardStep("lead"); }} disabled={uploading || loadingPreview}>
                      {t("contact_import_next_leads")}
                    </button>
                  </>
                )}

                {wizardStep === "lead" && (
                  <>
                    <button type="button" className="btn btn-outline-secondary" onClick={() => setWizardStep("contact")} disabled={uploading}>{t("contact_import_back_contacts")}</button>
                    <button type="button" className="btn btn-primary" onClick={() => { setIncludeLeads(true); handleImport("contacts_leads"); }} disabled={uploading || loadingPreview}>
                      {uploading ? `${t("loading")}...` : t("contact_import_contacts_and_leads")}
                    </button>
                    <button type="button" className="btn btn-outline-primary" onClick={() => { setIncludeLeads(true); setIncludeTasks(true); setWizardStep("task"); }} disabled={uploading || loadingPreview}>
                      {t("contact_import_next_tasks")}
                    </button>
                  </>
                )}

                {wizardStep === "task" && (
                  <>
                    <button type="button" className="btn btn-outline-secondary" onClick={() => setWizardStep("lead")} disabled={uploading}>{t("contact_import_back_leads")}</button>
                    <button type="button" className="btn btn-primary" onClick={() => { setIncludeLeads(true); setIncludeTasks(true); handleImport("contacts_leads_tasks"); }} disabled={uploading || loadingPreview}>
                      {uploading ? `${t("loading")}...` : t("contact_import_contacts_leads_tasks")}
                    </button>
                  </>
                )}
              </div>
            </div>
          )}

          {uploadError && <div className="contactImportAlert contactImportAlertDanger">{uploadError}</div>}

          {uploadResult && (
            <div className="contactImportResult">
              <div className="contactImportResultHeader">
                <div>
                  <strong>{t("contacts_import_result")}</strong>
                  <span>{uploadResult.created_contacts ?? uploadResult.created} {t("contact_import_contacts_count")}{uploadResult.created_leads ? ` • ${t("contact_import_leads_count", { count: uploadResult.created_leads })}` : ""}{uploadResult.created_tasks ? ` • ${t("contact_import_tasks_count", { count: uploadResult.created_tasks })}` : ""}{uploadResult.failed ? ` • ${t("failed")}: ${uploadResult.failed}` : ""}{uploadResult.warnings?.length ? ` • ${t("warnings")}: ${uploadResult.warnings.length}` : ""}</span>
                </div>
                {uploadResult.error_csv && <button type="button" className="btn btn-sm btn-outline-light" onClick={handleDownloadErrorCsv}>{t("download_error_csv")}</button>}
              </div>
            </div>
          )}
        </section>

        <aside className="contactImportCard contactImportTemplateCard contactImportSummaryCard">
          {preview ? (
            <>
              <div className="contactImportTemplateIcon">{completionPercent}%</div>
              <h2>{t("contact_import_readiness")}</h2>
              <div className="contactImportProgressBar"><span style={{ width: `${completionPercent}%` }} /></div>
              <div className="contactImportSummaryRows">
                <div><span>{t("contact_import_stage")}</span><strong>{stageTitle}</strong></div>
                <div><span>{t("contact_import_required_fields")}</span><strong>{activeStageStats.requiredMapped}/{activeStageStats.requiredTotal}</strong></div>
                <div><span>{t("crm_activities_slot_busy_title")}</span><strong>{activeStageStats.locked}</strong></div>
                <div><span>{t("contact_import_system_fields")}</span><strong>{activeStageStats.system}</strong></div>
                <div><span>{t("contact_import_dynamic_fields_short")}</span><strong>{activeStageStats.custom}</strong></div>
                <div><span>{t("contact_import_will_be_created")}</span><strong>{activeStageStats.newFields}</strong></div>
                {wizardStep === "task" && <div><span>{t("details")}</span><strong>{taskDetailSources.length}</strong></div>}
              </div>
              <div className="contactImportLegend">
                <p><b className="dot required" /> {t("contact_import_legend_required")}</p>
                <p><b className="dot orRequired" /> {t("contact_import_legend_channel")}</p>
                <p><b className="dot custom" /> {t("contact_import_legend_dynamic")}</p>
                <p><b className="dot new" /> {t("contact_import_legend_new")}</p>
                <p><b className="dot locked" /> {t("contact_import_legend_used")}</p>
              </div>
            </>
          ) : (
            <>
              <div className="contactImportTemplateIcon">{t("lead_origin_csv_label")}</div>
              <h2>{t("contact_import_template_title")}</h2>
              <p>{t("contact_import_template_text")}</p>
              <button type="button" className="btn btn-primary w-100" onClick={downloadTemplateCsv}>{t("contact_import_download_template")}</button>
              <div className="contactImportMiniGuide">
                <div><span>1</span><p>{t("contact_import_step_upload")}</p></div>
                <div><span>2</span><p>{t("contact_import_map_columns")}</p></div>
                <div><span>3</span><p>{t("contact_import_finish_or_continue")}</p></div>
              </div>
            </>
          )}
        </aside>
      </div>

      {uploadResult?.warnings?.length > 0 && (
        <section className="contactImportCard contactImportWarnings"><h3>{t("contacts_import_warnings_title")}</h3><ul>{uploadResult.warnings.map((w, idx) => <li key={idx}>{w}</li>)}</ul></section>
      )}

      {uploadResult?.error_rows?.length > 0 && (
        <section className="contactImportCard"><h3>{t("contacts_import_errors_table")}</h3><div className="contactImportTableWrap"><table className="table table-sm table-bordered contactImportTable"><thead><tr><th>#</th><th>{t("row")}</th><th>full_name</th><th>emails</th><th>phones</th><th>{t("error")}</th></tr></thead><tbody>{uploadResult.error_rows.map((er, idx) => { const data = er.data || {}; return <tr key={idx}><td>{idx + 1}</td><td>{er.row_index}</td><td>{data.full_name || ""}</td><td>{data.emails || data.email || ""}</td><td>{data.phones || data.phone || ""}</td><td className="text-danger small">{er.error}</td></tr>; })}</tbody></table></div></section>
      )}
    </div>
  );
}

export default ContactImportPage;
